import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import axios from '../utils/axiosConfig';
import { formatCurrency, formatCurrencyShort } from '../utils/currency';
import { formatDate, addMonthsIso, parseToDate } from '../utils/dates';
import { t } from '../utils/translations';
import { lineGstBreakdown, GST_RATES, gstRateSelectValue, gstRateNumber } from '../utils/gst';
import { useAuth } from '../context/AuthContext';
import DateInput from '../components/DateInput';
import InvoicePrint from '../components/InvoicePrint';
import SearchableCustomerSelect from '../components/SearchableCustomerSelect';
import SearchableProductSelect from '../components/SearchableProductSelect';
import { invoiceBreakdown, isRealSale, isPaidSale, calculateProfit } from '../utils/profit';
import { sendInvoiceOnWhatsApp, shareInvoicePdfOnWhatsApp } from '../utils/whatsapp';
import './Invoices.css';

const Invoices = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const clientGstOn = Boolean(user?.gst_enabled);
  // When client GST is off, admin/super_admin can opt into a GST bill for this invoice only
  const canCreateGstBill = isAdmin && !clientGstOn;
  const [applyGstBill, setApplyGstBill] = useState(false);
  const gstEnabled = clientGstOn || (canCreateGstBill && applyGstBill);
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]); // Store all invoices for filtering
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);
  const [printCustomer, setPrintCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    tax_id: '',
    status: 'active'
  });
  const [searchFilters, setSearchFilters] = useState({
    invoiceNumber: '',
    customerName: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: ''
  });
  const [listMode, setListMode] = useState('regular'); // regular | dummy
  const [formData, setFormData] = useState({
    customer_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: addMonthsIso(new Date(), 1),
    discount: 0,
    notes: '',
    status: 'draft',
    is_dummy: false,
    items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, discount: 0, gst_rate: 0, gst_inclusive: false }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, productsRes, customersRes] = await Promise.all([
        axios.get('/api/invoices'),
        axios.get('/api/products'),
        axios.get('/api/customers')
      ]);

      const invoicesData = invoicesRes.data?.invoices || [];
      setInvoices(invoicesData);
      setAllInvoices(invoicesData); // Store all invoices
      setProducts(productsRes.data?.products || []);
      setCustomers(customersRes.data?.customers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set empty arrays to prevent undefined errors
      setInvoices([]);
      setAllInvoices([]);
      setProducts([]);
      setCustomers([]);
      alert(t('Failed to fetch data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        is_dummy: Boolean(formData.is_dummy),
        discount: parseFloat(formData.discount || 0),
        apply_gst: gstEnabled,
        items: formData.items
          .filter(item => item.description || item.product_id)
          .map(({ qty_uom, qty_base, conversion_to_base, ...item }) => ({
            // qty_uom / qty_base / conversion_to_base are derived on the server from uom_id + quantity,
            // so stale values from a saved line can never override an edited quantity or unit.
            ...item,
            gst_rate: gstEnabled ? gstRateNumber(item.gst_rate) : 0,
            gst_inclusive: gstEnabled ? Boolean(item.gst_inclusive) : false
          }))
      };

      let response;
      if (editingInvoice) {
        response = await axios.put(`/api/invoices/${editingInvoice.id}`, payload);
      } else {
        response = await axios.post('/api/invoices', payload);
      }

      setShowModal(false);
      setEditingInvoice(null);
      
      // Fetch the complete invoice data for printing
      if (response.data && response.data.invoice) {
        const savedInvoice = response.data.invoice;
        try {
          // Fetch full invoice details with items
          const fullResponse = await axios.get(`/api/invoices/${savedInvoice.id}`);
          if (fullResponse.data && fullResponse.data.invoice) {
            const fullInvoice = fullResponse.data.invoice;
            const customer = customers.find(c => c.id === fullInvoice.customer_id);
            setPrintInvoice(fullInvoice);
            setPrintCustomer(customer || null);
          }
        } catch (err) {
          // Fallback to saved invoice
          const customer = customers.find(c => c.id === savedInvoice.customer_id);
          setPrintInvoice(savedInvoice);
          setPrintCustomer(customer || null);
        }
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save invoice'));
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    const hasGst = Number(invoice.tax_amount) > 0 || (invoice.items || []).some((i) => Number(i.gst_rate) > 0);
    setApplyGstBill(canCreateGstBill ? hasGst : false);
    setFormData({
      customer_id: invoice.customer_id || '',
      issue_date: invoice.issue_date || new Date().toISOString().split('T')[0],
      due_date: parseToDate(invoice.due_date)
        ? invoice.due_date
        : addMonthsIso(invoice.issue_date || new Date(), 1),
      discount: invoice.discount || 0,
      notes: invoice.notes || '',
      status: invoice.status || 'draft',
      is_dummy: Boolean(Number(invoice.is_dummy)),
      items: invoice.items && invoice.items.length > 0
        ? invoice.items.map(item => ({
            ...item,
            gst_rate: gstRateNumber(item.gst_rate),
            gst_inclusive: Boolean(Number(item.gst_inclusive))
          }))
        : [{ product_id: '', description: '', quantity: 1, unit_price: 0, discount: 0, gst_rate: 0, gst_inclusive: false }]
    });
    setShowModal(true);
  };

  const handlePrint = async (invoice) => {
    try {
      // Fetch full invoice details with customer info
      const response = await axios.get(`/api/invoices/${invoice.id}`);
      if (response.data && response.data.invoice) {
        const fullInvoice = response.data.invoice;
        const customer = customers.find(c => c.id === fullInvoice.customer_id);
        setPrintInvoice(fullInvoice);
        setPrintCustomer(customer || null);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      // Fallback to using invoice data from list
      const customer = customers.find(c => c.id === invoice.customer_id);
      setPrintInvoice(invoice);
      setPrintCustomer(customer || null);
    }
  };

  const handleWhatsApp = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice.id}`);
      const fullInvoice = response.data?.invoice || invoice;
      const customer = customers.find((c) => c.id === fullInvoice.customer_id) || null;
      const result = await shareInvoicePdfOnWhatsApp(fullInvoice, customer);
      if (result.mode === 'download+text') {
        alert('PDF downloaded. Attach it in WhatsApp (the message is ready).');
      }
    } catch (error) {
      console.error('Error sharing invoice PDF on WhatsApp:', error);
      const customer = customers.find((c) => c.id === invoice.customer_id) || null;
      try {
        const result = await shareInvoicePdfOnWhatsApp(invoice, customer);
        if (result.mode === 'download+text') {
          alert('PDF downloaded. Attach it in WhatsApp (the message is ready).');
        }
      } catch (e2) {
        sendInvoiceOnWhatsApp(invoice, customer);
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this invoice?'))) {
      return;
    }

    try {
      await axios.delete(`/api/invoices/${id}`);
      fetchData();
    } catch (error) {
      alert(t('Failed to delete invoice'));
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', description: '', quantity: 1, unit_price: 0, discount: 0, gst_rate: 0, gst_inclusive: false }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      items: newItems.length > 0
        ? newItems
        : [{ product_id: '', description: '', quantity: 1, unit_price: 0, discount: 0, gst_rate: 0, gst_inclusive: false }]
    });
  };

  // Price per selected unit: the unit's own sale price, else base price x conversion.
  const uomUnitPrice = (uom, basePrice) => {
    if (uom && uom.sale_price != null && uom.sale_price !== '') return parseFloat(uom.sale_price) || 0;
    const factor = parseFloat(uom && uom.conversion_to_base) || 1;
    return Math.round((parseFloat(basePrice) || 0) * factor * 1000) / 1000;
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };

    // If product is selected, auto-fill description, price and GST
    if (field === 'product_id' && value) {
      const product = products.find(p => p.id === value);
      if (product) {
        const uoms = product.uoms || [];
        const defaultUom = uoms.find((u) => Number(u.is_sales_default))
          || uoms.find((u) => Number(u.is_base))
          || uoms[0]
          || null;
        newItems[index].description = product.name;
        newItems[index].uom_id = defaultUom ? defaultUom.id : undefined;
        newItems[index].unit_price = uomUnitPrice(defaultUom, product.price);
        newItems[index].gst_rate = product.gst_rate || 0;
        newItems[index].gst_inclusive = Boolean(Number(product.gst_inclusive));
      }
    }

    // Unit changed: new lines get that unit's price. Saved lines keep the price already billed.
    if (field === 'uom_id' && value && !newItems[index].id) {
      const product = products.find(p => p.id === newItems[index].product_id);
      const uom = product && (product.uoms || []).find((u) => u.id === value);
      if (uom) newItems[index].unit_price = uomUnitPrice(uom, product.price);
    }

    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    let taxable = 0;
    let taxAmount = 0;
    let cgstAmount = 0;
    let sgstAmount = 0;
    let gross = 0;
    let cgstRate = 0;
    let sgstRate = 0;

    formData.items.forEach(item => {
      if (!(item.description || item.product_id)) return;
      const breakdown = lineGstBreakdown(
        item.quantity,
        item.unit_price,
        item.discount || 0,
        gstEnabled ? gstRateNumber(item.gst_rate) : 0,
        gstEnabled ? Boolean(item.gst_inclusive) && gstRateNumber(item.gst_rate) > 0 : false
      );
      taxable += breakdown.taxable;
      taxAmount += breakdown.gst;
      cgstAmount += breakdown.cgst;
      sgstAmount += breakdown.sgst;
      gross += breakdown.lineTotal;
      if (breakdown.cgstRate) cgstRate = breakdown.cgstRate;
      if (breakdown.sgstRate) sgstRate = breakdown.sgstRate;
    });

    const discount = parseFloat(formData.discount || 0);
    return {
      subtotal: taxable,
      taxAmount,
      cgstAmount,
      sgstAmount,
      cgstRate,
      sgstRate,
      total: gross - discount
    };
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: addMonthsIso(new Date(), 1),
      discount: 0,
      notes: '',
      status: 'draft',
      is_dummy: false,
      items: [{ product_id: '', description: '', quantity: 1, unit_price: 0, discount: 0, gst_rate: 0, gst_inclusive: false }]
    });
  };

  const openModal = () => {
    resetForm();
    setApplyGstBill(false);
    setEditingInvoice(null);
    setShowModal(true);
  };

  const openDummyModal = () => {
    resetForm();
    setFormData((prev) => ({
      ...prev,
      is_dummy: true,
      status: 'paid'
    }));
    setApplyGstBill(false);
    setEditingInvoice(null);
    setListMode('dummy');
    setShowModal(true);
  };

  // Filter invoices based on search criteria
  useEffect(() => {
    let filtered = [...allInvoices].filter((invoice) => {
      const dummy = Boolean(Number(invoice.is_dummy));
      return listMode === 'dummy' ? dummy : !dummy;
    });

    // Filter by invoice number
    if (searchFilters.invoiceNumber) {
      filtered = filtered.filter(invoice =>
        invoice.invoice_number?.toLowerCase().includes(searchFilters.invoiceNumber.toLowerCase())
      );
    }

    // Filter by customer name
    if (searchFilters.customerName) {
      filtered = filtered.filter(invoice =>
        invoice.customer_name?.toLowerCase().includes(searchFilters.customerName.toLowerCase())
      );
    }

    // Filter by status
    if (searchFilters.status) {
      filtered = filtered.filter(invoice => invoice.status === searchFilters.status);
    }

    // Filter by date range
    if (searchFilters.dateFrom) {
      filtered = filtered.filter(invoice => invoice.issue_date >= searchFilters.dateFrom);
    }
    if (searchFilters.dateTo) {
      filtered = filtered.filter(invoice => invoice.issue_date <= searchFilters.dateTo);
    }

    // Filter by amount range
    if (searchFilters.amountMin) {
      filtered = filtered.filter(invoice => (invoice.total_amount || 0) >= parseFloat(searchFilters.amountMin));
    }
    if (searchFilters.amountMax) {
      filtered = filtered.filter(invoice => (invoice.total_amount || 0) <= parseFloat(searchFilters.amountMax));
    }

    setInvoices(filtered);
  }, [searchFilters, allInvoices, listMode]);

  // Update search filters
  const handleSearchChange = (field, value) => {
    setSearchFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear all search filters
  const clearSearch = () => {
    setSearchFilters({
      invoiceNumber: '',
      customerName: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: ''
    });
  };

  // Handle add new customer from invoice form
  const handleAddNewCustomer = () => {
    setCustomerFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
      tax_id: '',
      status: 'active'
    });
    setShowCustomerModal(true);
  };

  // Handle customer form submit
  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/customers', customerFormData);
      if (response.data && response.data.customer) {
        // Refresh customers list
        const customersRes = await axios.get('/api/customers');
        setCustomers(customersRes.data.customers || []);
        
        // Select the newly created customer
        setFormData({ ...formData, customer_id: response.data.customer.id });
        
        // Close customer modal
        setShowCustomerModal(false);
        setCustomerFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zip_code: '',
          country: '',
          tax_id: '',
          status: 'active'
        });
      }
    } catch (error) {
      alert(error.response?.data?.message || t('Failed to save customer'));
    }
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  const totals = calculateTotal();

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{listMode === 'dummy' ? 'Dummy Invoices' : t('Invoices')}</h1>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Link to="/pos" className="btn btn-secondary">
              POS Sale
            </Link>
            <Link to="/returns" className="btn btn-secondary">
              Returns
            </Link>
            {listMode === 'dummy' ? (
              <button type="button" onClick={openDummyModal} className="btn btn-primary">
                Create Dummy Invoice
              </button>
            ) : (
              <button type="button" onClick={openModal} className="btn btn-primary">
                {t('Create Invoice')}
              </button>
            )}
          </div>
        </div>

        <div className="invoice-list-tabs">
          <button
            type="button"
            className={`invoice-list-tab ${listMode === 'regular' ? 'active' : ''}`}
            onClick={() => setListMode('regular')}
          >
            Invoices
          </button>
          <button
            type="button"
            className={`invoice-list-tab ${listMode === 'dummy' ? 'active' : ''}`}
            onClick={() => setListMode('dummy')}
          >
            Dummy Invoices
          </button>
        </div>

        {listMode === 'dummy' ? (
          <div className="invoice-dummy-banner">
            Dummy invoices can use past dates, do not deduct stock, and are excluded from dashboard/report totals.
          </div>
        ) : null}

        {/* Search and Filter Section */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="search-header">
            <h3>{t('Search Invoices')}</h3>
            <button onClick={clearSearch} className="btn btn-secondary btn-sm">
              {t('Clear')}
            </button>
          </div>
          <div className="search-filters">
            <div className="search-row">
              <div className="form-group">
                <label>{t('Invoice Number')}</label>
                <input
                  type="text"
                  placeholder={t('Search by invoice number')}
                  value={searchFilters.invoiceNumber}
                  onChange={(e) => handleSearchChange('invoiceNumber', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('Customer Name')}</label>
                <input
                  type="text"
                  placeholder={t('Search by customer name')}
                  value={searchFilters.customerName}
                  onChange={(e) => handleSearchChange('customerName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('Status')}</label>
                <select
                  value={searchFilters.status}
                  onChange={(e) => handleSearchChange('status', e.target.value)}
                >
                  <option value="">{t('All Statuses')}</option>
                  <option value="draft">{t('Draft')}</option>
                  <option value="sent">{t('Sent')}</option>
                  <option value="pending">Pending</option>
                  <option value="paid">{t('Paid')}</option>
                  <option value="overdue">{t('Overdue')}</option>
                  <option value="cancelled">{t('Cancelled')}</option>
                </select>
              </div>
            </div>
            <div className="search-row">
              <div className="form-group">
                <label>{t('Date From')}</label>
                <DateInput
                  value={searchFilters.dateFrom}
                  onChange={(e) => handleSearchChange('dateFrom', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('Date To')}</label>
                <DateInput
                  value={searchFilters.dateTo}
                  onChange={(e) => handleSearchChange('dateTo', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('Amount From')}</label>
                <input
                  type="number"
                  step="0.001"
                  placeholder={t('Min amount')}
                  value={searchFilters.amountMin}
                  onChange={(e) => handleSearchChange('amountMin', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>{t('Amount To')}</label>
                <input
                  type="number"
                  step="0.001"
                  placeholder={t('Max amount')}
                  value={searchFilters.amountMax}
                  onChange={(e) => handleSearchChange('amountMax', e.target.value)}
                />
              </div>
            </div>
            <div className="search-results-info">
              <span>
                {t('Showing')} {invoices.length} {t('of')} {allInvoices.length} {t('invoices')}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          {isAdmin && (() => {
            const paid = invoices.filter(isPaidSale);
            const unpaid = invoices.filter((inv) => isRealSale(inv) && !isPaidSale(inv));
            const total = calculateProfit(paid, products);
            return (
              <div style={{ marginBottom: 12, fontSize: 14 }}>
                <strong>Profit on paid invoices in this list:</strong>{' '}
                <span style={{ color: total < 0 ? '#b91c1c' : '#047857', fontWeight: 700 }}>{formatCurrency(total)}</span>
                <span style={{ color: '#6b7280' }}>
                  {' '}({paid.length} paid{unpaid.length ? `, ${unpaid.length} unpaid not counted` : ''}).
                  {' '}Dummy and cancelled invoices are excluded. “~” = older sale with estimated cost, “⚠” = deleted product.
                </span>
              </div>
            );
          })()}
          <table className="table">
            <thead>
              <tr>
                <th>{t('Invoice #')}</th>
                <th>{t('Customer')}</th>
                <th>{t('Issue Date')}</th>
                <th>{t('Due Date')}</th>
                <th>{t('Total Amount')}</th>
                {isAdmin && <th>{t('Profit')}</th>}
                <th>{t('Status')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} style={{ textAlign: 'center' }}>
                    {listMode === 'dummy'
                      ? 'No dummy invoices found'
                      : (allInvoices.filter((i) => !Number(i.is_dummy)).length === 0
                        ? t('No invoices found')
                        : t('No invoices match your search criteria'))}
                  </td>
                </tr>
              ) : (
                invoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td>
                      {invoice.invoice_number}
                      {Number(invoice.is_dummy) ? (
                        <span className="status-badge status-dummy" style={{ marginLeft: 6 }}>Dummy</span>
                      ) : null}
                    </td>
                    <td>{invoice.customer_name || 'N/A'}</td>
                    <td>{formatDate(invoice.issue_date)}</td>
                    <td>{formatDate(invoice.due_date)}</td>
                    <td>{formatCurrency(invoice.total_amount || 0)}</td>
                    {isAdmin && (() => {
                      // Dummy and cancelled invoices are not real sales
                      const b = isRealSale(invoice) ? invoiceBreakdown(invoice, products) : null;
                      if (!b) return <td>—</td>;
                      const note = [
                        `Sales ${formatCurrency(b.revenue)}${(invoice.items || []).some((i) => Number(i.gst_rate) > 0) ? ' (ex GST)' : ''}`,
                        b.discount ? `Invoice discount ${formatCurrency(b.discount)}` : null,
                        `Cost ${formatCurrency(b.cost)}`,
                        b.estimated ? 'Older sale: cost estimated from the product\'s current cost' : null,
                        b.missingCost ? 'A product on this invoice was deleted: its cost is missing, so profit is overstated' : null
                      ].filter(Boolean).join('\n');
                      return (
                        <td
                          title={note}
                          style={{
                            color: b.profit < 0 ? '#b91c1c' : '#047857',
                            fontWeight: 600,
                            opacity: isPaidSale(invoice) ? 1 : 0.65
                          }}
                        >
                          {formatCurrency(b.profit)}
                          {b.missingCost
                            ? <span style={{ color: '#b45309' }}> ⚠</span>
                            : b.estimated && <span style={{ color: '#6b7280', fontWeight: 400 }}> ~</span>}
                        </td>
                      );
                    })()}
                    <td>
                      <span className={`status-badge status-${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>
                      <KebabMenu
                        menuId={`invoice-${invoice.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        label={t('Actions')}
                        items={[
                          { label: t('Print'), icon: MENU_ICONS.print, onClick: () => handlePrint(invoice) },
                          { label: 'WhatsApp PDF', icon: MENU_ICONS.whatsapp, onClick: () => handleWhatsApp(invoice) },
                          !Number(invoice.is_dummy) && {
                            label: 'Return',
                            icon: MENU_ICONS.returns,
                            onClick: () => navigate(`/returns?invoice=${invoice.id}`)
                          },
                          { label: t('Edit'), icon: MENU_ICONS.edit, onClick: () => handleEdit(invoice) },
                          { label: t('Delete'), icon: MENU_ICONS.trash, danger: true, onClick: () => handleDelete(invoice.id) }
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '1200px', width: '95%', maxHeight: '95vh' }}>
              <div className="modal-header">
                <h2>
                  {editingInvoice
                    ? (formData.is_dummy ? 'Edit Dummy Invoice' : t('Edit Invoice'))
                    : (formData.is_dummy ? 'Create Dummy Invoice' : t('Create Invoice'))}
                </h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                {formData.is_dummy ? (
                  <div className="invoice-dummy-banner" style={{ marginBottom: 12 }}>
                    Dummy mode: past dates allowed · stock not deducted · excluded from dashboard & reports
                  </div>
                ) : null}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t('Customer')} *</label>
                    <SearchableCustomerSelect
                      customers={customers || []}
                      value={formData.customer_id || ''}
                      onChange={(customerId) => setFormData({ ...formData, customer_id: customerId })}
                      onAddNew={handleAddNewCustomer}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('Status')}</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="draft">{t('Draft')}</option>
                      <option value="sent">{t('Sent')}</option>
                      <option value="pending">Pending</option>
                      <option value="paid">{t('Paid')}</option>
                      <option value="overdue">{t('Overdue')}</option>
                      <option value="cancelled">{t('Cancelled')}</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t('Issue Date')} *{formData.is_dummy ? ' (past dates OK)' : ''}</label>
                    <DateInput
                      value={formData.issue_date}
                      onChange={(e) => {
                        const issue_date = e.target.value;
                        setFormData((prev) => {
                          const autoDue = addMonthsIso(prev.issue_date, 1);
                          const dueWasAuto = !prev.due_date || prev.due_date === autoDue;
                          return {
                            ...prev,
                            issue_date,
                            due_date: dueWasAuto ? addMonthsIso(issue_date, 1) : prev.due_date
                          };
                        });
                      }}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('Due Date')}</label>
                    <DateInput
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    />
                  </div>
                </div>

                {canCreateGstBill && (
                  <div className="form-group" style={{
                    marginTop: '10px',
                    padding: '12px 14px',
                    border: '1px solid #fde68a',
                    background: '#fffbeb',
                    borderRadius: '8px'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={applyGstBill}
                        onChange={(e) => setApplyGstBill(e.target.checked)}
                      />
                      <span>
                        <strong>Create GST bill</strong>
                        <span style={{ display: 'block', fontSize: '12px', color: '#92400e', fontWeight: 400 }}>
                          Client GST is disabled. Enable this to calculate CGST/SGST on this invoice only (admin).
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label><strong>{t('Items')}</strong></label>
                    <button type="button" onClick={addItem} className="btn btn-success btn-sm">
                      {t('Add Item')}
                    </button>
                  </div>
                  {formData.items.map((item, index) => (
                    <div key={index} className="invoice-item-row" style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr 1fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end', marginBottom: '10px' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>{t('Product')}</label>
                          <SearchableProductSelect
                            products={products || []}
                            value={item.product_id || ''}
                            onChange={(productId) => updateItem(index, 'product_id', productId)}
                          />
                          {(() => {
                            const lineProduct = products.find((p) => p.id === item.product_id);
                            const uoms = (lineProduct && lineProduct.uoms) || [];
                            if (uoms.length < 2) return null;
                            const baseCode = (uoms.find((u) => Number(u.is_base)) || uoms[0]).code;
                            return (
                              <select
                                value={item.uom_id || ''}
                                onChange={(e) => updateItem(index, 'uom_id', e.target.value)}
                                style={{ marginTop: 6, width: '100%' }}
                                title="Unit this quantity and price are for"
                              >
                                {uoms.map((u) => (
                                  <option key={u.id} value={u.id}>
                                    {u.code}
                                    {Number(u.is_base) ? ' (base)' : ` = ${u.conversion_to_base} ${baseCode}`}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>{t('Description')}</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                            required
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>{t('Qty')}</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                            required
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>{t('Price')}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            required
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>{t('Discount')}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={item.discount}
                            onChange={(e) => updateItem(index, 'discount', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label>{t('Total')}</label>
                          <input
                            type="text"
                            value={formatCurrencyShort((item.quantity * item.unit_price) - (item.discount || 0))}
                            readOnly
                            style={{ backgroundColor: '#f5f5f5' }}
                          />
                        </div>
                        {formData.items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn btn-danger btn-sm"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      {gstEnabled && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '10px', marginTop: '8px' }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>GST %</label>
                            <select
                              value={gstRateSelectValue(item.gst_rate, '0')}
                              onChange={(e) => updateItem(index, 'gst_rate', parseFloat(e.target.value) || 0)}
                            >
                              {GST_RATES.map((rate) => (
                                <option key={rate} value={rate}>{rate}%</option>
                              ))}
                            </select>
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Price includes GST?</label>
                            <select
                              value={item.gst_inclusive ? '1' : '0'}
                              onChange={(e) => updateItem(index, 'gst_inclusive', e.target.value === '1')}
                            >
                              <option value="0">Exclude (add GST)</option>
                              <option value="1">Include (GST in price)</option>
                            </select>
                          </div>
                          <div style={{ fontSize: '12px', color: '#555', alignSelf: 'end', paddingBottom: '8px' }}>
                            {(() => {
                              const b = lineGstBreakdown(
                                item.quantity,
                                item.unit_price,
                                item.discount || 0,
                                gstRateNumber(item.gst_rate),
                                Boolean(item.gst_inclusive) && gstRateNumber(item.gst_rate) > 0
                              );
                              if (b.gst > 0) {
                                return `Line: base ${formatCurrencyShort(b.taxable)} + CGST ${formatCurrencyShort(b.cgst)} + SGST ${formatCurrencyShort(b.sgst)} = ${formatCurrencyShort(b.lineTotal)}`;
                              }
                              return `Line: ${formatCurrencyShort(b.lineTotal)} (no GST)`;
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span>{t('Subtotal')}:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {gstEnabled && totals.taxAmount > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span>CGST{totals.cgstRate ? ` (${totals.cgstRate}%)` : ''}:</span>
                        <span>{formatCurrency(totals.cgstAmount)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <span>SGST{totals.sgstRate ? ` (${totals.sgstRate}%)` : ''}:</span>
                        <span>{formatCurrency(totals.sgstAmount)}</span>
                      </div>
                    </>
                  )}
                  <div className="form-group" style={{ marginBottom: '10px' }}>
                    <label>{t('Discount')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.discount}
                      onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold' }}>
                    <span>{t('Total')}:</span>
                    <span>{formatCurrency(totals.total)}</span>
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '20px' }}>
                  <label>{t('Notes')}</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows="3"
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingInvoice ? t('Update') : t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {printInvoice && (
          <InvoicePrint
            invoice={printInvoice}
            customer={printCustomer}
            onClose={() => {
              setPrintInvoice(null);
              setPrintCustomer(null);
            }}
          />
        )}

        {/* Add Customer Modal */}
        {showCustomerModal && (
          <div className="modal">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h2>{t('Add New Customer')}</h2>
                <button className="close-btn" onClick={() => setShowCustomerModal(false)}>×</button>
              </div>
              <form onSubmit={handleCustomerSubmit}>
                <div className="form-group">
                  <label>{t('Name')} *</label>
                  <input
                    type="text"
                    value={customerFormData.name}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t('Email')}</label>
                    <input
                      type="email"
                      value={customerFormData.email}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('Phone')} / {t('Mobile')} *</label>
                    <input
                      type="text"
                      value={customerFormData.phone}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('Address')}</label>
                  <input
                    type="text"
                    value={customerFormData.address}
                    onChange={(e) => setCustomerFormData({ ...customerFormData, address: e.target.value })}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t('City')}</label>
                    <input
                      type="text"
                      value={customerFormData.city}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, city: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('State')}</label>
                    <input
                      type="text"
                      value={customerFormData.state}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, state: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('Zip Code')}</label>
                    <input
                      type="text"
                      value={customerFormData.zip_code}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, zip_code: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label>{t('Country')}</label>
                    <input
                      type="text"
                      value={customerFormData.country}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, country: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('Tax ID')}</label>
                    <input
                      type="text"
                      value={customerFormData.tax_id}
                      onChange={(e) => setCustomerFormData({ ...customerFormData, tax_id: e.target.value })}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button type="button" onClick={() => setShowCustomerModal(false)} className="btn btn-secondary">
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {t('Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Invoices;
